import { isEmpty } from 'ramda'
import { cloneDeep } from 'lodash'
import { changeSelectedSla } from '@vtex/delivery-packages/dist/sla'
import { filterLogisticsInfo } from '@vtex/delivery-packages/dist/shipping'

import { useShipping } from '../../../../../../core/globalState/shipping'
import { useVendor } from '../../../../../../core/globalState/vendor'
import {
  getCurrentAddress,
  getUniquePickupSlasFromLogisticsInfo,
  mergeLogisticsInfoWithPickupSlasMap,
} from '../../utils'
import type { Package } from '../../../../../../core/typings'
import { useShippingActionsLegacy } from '../../../../../../checkout-instore-bridge/hooks/shippingActionsLegacy'
import type {
  OrderForm,
  OrderFormItem,
} from '../../../../../../core/requests/checkout'
import { useOrderForm } from '../../../../../../core/requests/checkout'
import { DeliveryChannel, ShippingMethod } from '../../../../constants'
import { excludeCheckInFromSlas } from '../../../../utils/shipping'
import {
  checkAccountHasTakeAwayAvailable,
  getSelectedSlaFromLogisticInfoItem,
} from '../../../../../../core/utils/orderForm'
import type { OrderFormItemWithAssemblyItems } from '../../../Cart/typings'
import { groupItemsWithAssemblyItems } from '../../../Cart/utils/groupItemsWithAssemblyItems'
import { logger } from '../../../../../../logger'

export type UsePickupPointSelectionProps = {
  item: Package['items'][0]
  selectedShippingMethod: ShippingMethod
}

export const useShippingPackageItem = ({
  item,
  selectedShippingMethod,
}: UsePickupPointSelectionProps) => {
  const { shipping } = useShipping()
  const { orderForm } = useOrderForm()

  const shouldShowUnavailableForPickupAlert = (() => {
    const logisticInfoItem = shipping.logisticsInfo?.find((logisticInfo) => {
      return logisticInfo.itemId === item.id
    })

    if (!logisticInfoItem?.slas || !orderForm) {
      return false
    }

    const slas = excludeCheckInFromSlas(logisticInfoItem?.slas, orderForm)

    return (
      selectedShippingMethod === ShippingMethod.PickUp &&
      !shipping.loading &&
      !slas.some((sla) => sla.deliveryChannel === DeliveryChannel.PickupInPoint)
    )
  })()

  const currentPackage = orderForm?.shippingData.logisticsInfo[item.index]

  const shouldShowForcingTakeAwayAlert =
    currentPackage?.stockProvider === 'InHands'

  const shouldShowUnavailableAlert =
    !currentPackage?.slas || isEmpty(currentPackage?.slas)

  return {
    shouldShowUnavailableForPickupAlert,
    shouldShowForcingTakeAwayAlert,
    shouldShowUnavailableAlert,
  }
}

export type UseChangeShippingMethodDrawerProps = {
  item: Package['items'][0]
  currentPackage: Package
}

export const useChangeShippingMethodDrawer = ({
  item,
  currentPackage,
}: UseChangeShippingMethodDrawerProps) => {
  const { shipping } = useShipping()
  const { vendor } = useVendor()
  const { sendLogisticsInfoOnShippingData } = useShippingActionsLegacy()
  const { orderForm, updateCartItem } = useOrderForm()

  const storeAddress = (() => {
    if (!vendor.store) {
      return null
    }

    return {
      street: vendor.store.address,
      city: vendor.store.city,
      country: vendor.store.country,
      neighborhood: vendor.store.neighborhood,
      postalCode: vendor.store.neighborhood,
      state: vendor.store.state,
      number: vendor.store.number,
    }
  })()

  const locationAddress = (() => {
    if (!isEmpty(shipping.fakeAddress)) {
      return shipping.fakeAddress
    }

    if (!isEmpty(shipping.address)) {
      return shipping.address
    }

    return getCurrentAddress(shipping.selectedAddresses, storeAddress)
  })()

  const logisticsInfoByCurrentLocation = mergeLogisticsInfoWithPickupSlasMap(
    shipping.logisticsInfo,
    shipping.locationPickupPointsContext.itemIndexToPickupSlasMap,
    locationAddress
  )

  const itemLogisticsInfoByCurrentLocation = filterLogisticsInfo(
    logisticsInfoByCurrentLocation,
    { items: [item] }
  ) as LogisticsInfo

  const pickupPointsByCurrentLocation = excludeCheckInFromSlas(
    getUniquePickupSlasFromLogisticsInfo(
      itemLogisticsInfoByCurrentLocation,
      locationAddress
    ),
    orderForm as OrderForm
  )

  const accountHasTakeAwayAvailable = checkAccountHasTakeAwayAvailable(
    orderForm
  )

  const packageHasTakeAwayAvailable = currentPackage.slas.some(
    (sla) => sla.pickupPointId === orderForm?.checkedInPickupPointId
  )

  const changeShippingIsTakeAwayForced = async (
    changedProductIndex: number
  ) => {
    const logisticInfo = orderForm?.shippingData.logisticsInfo.find(
      (l) => l.itemIndex === changedProductIndex
    )

    const isTakeAwayForced = logisticInfo?.stockProvider === 'InHands'

    if (isTakeAwayForced) {
      await updateCartItem(
        item.id,
        item.index,
        item.quantity,
        item.seller,
        false
      )
    }
  }

  const changeDeliveryMethodOfItemLogic = (
    itemWithAssemblyItems: OrderFormItemWithAssemblyItems,
    actualLogisticsInfo: LogisticsInfo | undefined,
    selectedDeliveryMethodSla: SLA
  ) => {
    if (actualLogisticsInfo === undefined) return actualLogisticsInfo
    const itemIndex = itemWithAssemblyItems.orderFormItem.index

    changeShippingIsTakeAwayForced(itemIndex)

    const logisticsInfo = actualLogisticsInfo.map((logisticInfoItem) => {
      return logisticInfoItem.itemIndex === itemIndex
        ? changeSelectedSla(logisticInfoItem, selectedDeliveryMethodSla)
        : logisticInfoItem
    })

    let newLogisticsInfo = logisticsInfo

    if (itemWithAssemblyItems.assemblyItems.length > 0) {
      itemWithAssemblyItems.assemblyItems.forEach((assemblyItem) => {
        changeShippingIsTakeAwayForced(assemblyItem.index)
      })

      newLogisticsInfo = logisticsInfo.map(
        (logisticInfoItem, logisticInfoItemIndex) => {
          return itemWithAssemblyItems.assemblyItems.find(
            (assemblyItem) => assemblyItem.index === logisticInfoItemIndex
          )
            ? changeSelectedSla(logisticInfoItem, selectedDeliveryMethodSla)
            : logisticInfoItem
        }
      )
    }

    return newLogisticsInfo
  }

  const changePickupPointOfItemLogic = (
    itemWithAssemblyItems: OrderFormItemWithAssemblyItems,
    actualLogisticsInfo: LogisticsInfo | undefined,
    selectedSlaId: string
  ) => {
    if (actualLogisticsInfo === undefined) return actualLogisticsInfo
    const itemIndex = itemWithAssemblyItems.orderFormItem.index

    changeShippingIsTakeAwayForced(itemIndex)

    const selectedSla = pickupPointsByCurrentLocation.find(
      (sla) => sla.id === selectedSlaId
    ) as SLA

    const logisticsInfo = actualLogisticsInfo.map((logisticInfoItem) => {
      return logisticInfoItem.itemIndex === itemIndex
        ? changeSelectedSla(logisticInfoItem, selectedSla)
        : logisticInfoItem
    })

    let newLogisticsInfo = logisticsInfo

    if (itemWithAssemblyItems.assemblyItems.length > 0) {
      itemWithAssemblyItems.assemblyItems.forEach((assemblyItem) => {
        changeShippingIsTakeAwayForced(assemblyItem.index)
      })

      newLogisticsInfo = logisticsInfo.map((locationLogisticInfoItem) => {
        return itemWithAssemblyItems.assemblyItems.find(
          (assemblyItem) =>
            assemblyItem.index === locationLogisticInfoItem.itemIndex
        )
          ? changeSelectedSla(locationLogisticInfoItem, selectedSla)
          : locationLogisticInfoItem
      })
    }

    return newLogisticsInfo
  }

  const changeItemToTakeAwayLogic = async (
    itemWithAssemblyItems: OrderFormItemWithAssemblyItems,
    actualLogisticsInfo: LogisticsInfo | undefined
  ) => {
    if (actualLogisticsInfo === undefined) return actualLogisticsInfo
    const shouldForceTakeAway = !packageHasTakeAwayAvailable
    const itemIndex = itemWithAssemblyItems.orderFormItem.index

    if (shouldForceTakeAway) {
      try {
        await updateCartItem(
          item.id,
          item.index,
          item.quantity,
          item.seller,
          true
        )

        if (itemWithAssemblyItems.assemblyItems.length > 0) {
          itemWithAssemblyItems.assemblyItems.forEach(async (assemblyItem) => {
            await updateCartItem(
              assemblyItem.item.id,
              assemblyItem.item.index,
              assemblyItem.item.quantity,
              assemblyItem.item.seller,
              true
            )
          })
        }
      } catch (err) {
        console.error(err)
        logger.error({
          workflowType: 'failure-to-set-feature-flag-context',
          error: err instanceof Error ? err : new Error(String(err)),
        })
      }

      return
    }

    const logisticsInfo = actualLogisticsInfo.map((logisticInfo) => {
      const isLogisticInfoToChange = logisticInfo.itemIndex === itemIndex

      const selectedSla = getSelectedSlaFromLogisticInfoItem(logisticInfo)

      const isTakeAway =
        selectedSla?.pickupPointId === orderForm?.checkedInPickupPointId

      if (!isLogisticInfoToChange || isTakeAway) {
        return logisticInfo
      }

      const slaTakeAway = logisticInfo.slas.find(
        (sla) => sla.pickupPointId === orderForm?.checkedInPickupPointId
      )

      return slaTakeAway
        ? changeSelectedSla(logisticInfo, slaTakeAway)
        : logisticInfo
    })

    let newLogisticsInfo = logisticsInfo

    if (itemWithAssemblyItems.assemblyItems.length > 0) {
      newLogisticsInfo = logisticsInfo?.map((logisticInfo) => {
        const isLogisticInfoToChange = !!itemWithAssemblyItems.assemblyItems.find(
          (assemblyItem) => assemblyItem.index === logisticInfo.itemIndex
        )

        const selectedSla = getSelectedSlaFromLogisticInfoItem(logisticInfo)

        const isTakeAway =
          selectedSla?.pickupPointId === orderForm?.checkedInPickupPointId

        if (!isLogisticInfoToChange || isTakeAway) {
          return logisticInfo
        }

        const slaTakeAway = logisticInfo.slas.find(
          (sla) => sla.pickupPointId === orderForm?.checkedInPickupPointId
        )

        return slaTakeAway
          ? changeSelectedSla(logisticInfo, slaTakeAway)
          : logisticInfo
      })
    }

    return newLogisticsInfo
  }

  const changeDeliveryMethodOfItem = async (
    itemId: string,
    selectedDeliveryMethodSla: SLA
  ) => {
    const itemsWithAssemblies = groupItemsWithAssemblyItems(
      orderForm?.items as Array<OrderFormItem & { index: number }>
    )

    let newLogisticInfo = cloneDeep(orderForm?.shippingData?.logisticsInfo)

    if (!newLogisticInfo) {
      return
    }

    itemsWithAssemblies.forEach((itemWithAssemblies) => {
      if (itemWithAssemblies.orderFormItem.item.id === itemId) {
        newLogisticInfo = changeDeliveryMethodOfItemLogic(
          itemWithAssemblies,
          newLogisticInfo,
          selectedDeliveryMethodSla
        )
      }
    })

    sendLogisticsInfoOnShippingData(newLogisticInfo, orderForm)
  }

  const changePickupPointOfItem = async (
    itemId: string,
    selectedSlaId: string
  ) => {
    const itemsWithAssemblies = groupItemsWithAssemblyItems(
      orderForm?.items as Array<OrderFormItem & { index: number }>
    )

    let newLogisticInfo = cloneDeep(orderForm?.shippingData?.logisticsInfo)

    if (!newLogisticInfo) {
      return
    }

    itemsWithAssemblies.forEach((itemWithAssemblies) => {
      if (itemWithAssemblies.orderFormItem.item.id === itemId) {
        newLogisticInfo = changePickupPointOfItemLogic(
          itemWithAssemblies,
          newLogisticInfo,
          selectedSlaId
        )
      }
    })

    sendLogisticsInfoOnShippingData(newLogisticInfo, orderForm)
  }

  const changeItemToTakeAway = async (itemId: string) => {
    const itemsWithAssemblies = groupItemsWithAssemblyItems(
      orderForm?.items as Array<OrderFormItem & { index: number }>
    )

    let newLogisticInfo = cloneDeep(orderForm?.shippingData?.logisticsInfo)

    if (!newLogisticInfo) {
      return
    }

    itemsWithAssemblies.forEach(async (itemWithAssemblies) => {
      if (itemWithAssemblies.orderFormItem.item.id === itemId) {
        newLogisticInfo = await changeItemToTakeAwayLogic(
          itemWithAssemblies,
          newLogisticInfo
        )
      }
    })

    sendLogisticsInfoOnShippingData(newLogisticInfo, orderForm)
  }

  const deliveryMethods =
    itemLogisticsInfoByCurrentLocation?.[0]?.slas.filter(
      (logisticInfoItem) =>
        logisticInfoItem.deliveryChannel === DeliveryChannel.Delivery
    ) ?? []

  return {
    changePickupPointOfItem,
    changeDeliveryMethodOfItem,
    deliveryMethods,
    pickupPointsByCurrentLocation,
    changeItemToTakeAway,
    accountHasTakeAwayAvailable,
    packageHasTakeAwayAvailable,
  }
}
